import { useCallback, useMemo } from 'react';

import { is } from 'utils/helpers';
import { round } from 'utils/math';
import {
	ENTITIES,
	makeSpacesNoBreak,
	type NoBreakSpaceVariant,
	parseNumber,
} from 'utils/string';

import { useLocale } from '../useSitecoreContext';

import { useNumberFormatter } from './useNumberFormatter';

// Optional leading minus or hyphen for negative numbers, then a digit
// followed by zero or more digits, whitespace, commas or periods, followed
// by a final digit. This won't match a single digit but that doesn't need
// any formatting anyway.
const priceNumbersRegex = new RegExp(
	String.raw`([${ENTITIES.minus}-]?\d[\d\s,.]*\d)`,
	'g',
);
// Same as above but with a negative lookahead at the end, disallowing any
// numbers to follow the match. The number also has to be at the very end.
const lastPriceNumberRegex = new RegExp(
	String.raw`[${ENTITIES.minus}-]?\d[\d\s,.]*\d(?!.*\d)$`,
	'g',
);

/**
 * Format price numbers according to the current locale.
 *
 * Affects all numbers in the supplied price, so a string like "1234 för 1234"
 * will have both numbers formatted.
 *
 * @example
 *
 * const formatPrice = usePriceFormatter();
 *
 * formatPrice(1234)
 * // => '1 234'
 *
 * formatPrice('3 för 12599.-')
 * // => '3 för 12 599.-'
 */
export function usePriceFormatter() {
	const { locale } = useLocale();
	const formatInteger = useNumberFormatter('integer');

	const currencyFormatter = useMemo(
		() =>
			new Intl.NumberFormat(locale, {
				style: 'currency',
				// Doesn't have to match the current market, the currency is not used.
				// It could be supplied as an environment variable like locale but it
				// would as of sep 2024 be risky since Norwegian Krone could have different
				// output if the server and client use different versions of ICU. This
				// would lead to hydration errors.
				// https://github.com/nodejs/node/issues/52376
				currency: 'EUR',
			}),
		[locale],
	);

	const formatPriceNumber = useCallback(
		(
			price: string | number | undefined,
			noBreakSpaceVariant: NoBreakSpaceVariant = 'narrow',
		): string => {
			if (is.nonZeroFalsy(price)) {
				return '';
			}
			const priceNum = parseNumber(price);
			// The currency formatter always has two fractions, so a price of 49 will
			// be formatted as 49,00. Use integer formatter to avoid this.
			const formatted = is.integer(priceNum)
				? formatInteger(priceNum)
				: currencyFormatter
						.formatToParts(priceNum)
						// The currency symbol can't be disabled so filter it out,
						// only the number itself is of interest.
						.filter((part) => part.type !== 'currency')
						.map((part) => part.value)
						.join('')
						.trim();
			return makeSpacesNoBreak(formatted, noBreakSpaceVariant);
		},
		[currencyFormatter, formatInteger],
	);

	return useCallback(
		(
			price: string | number | undefined,
			noBreakSpaceVariant: NoBreakSpaceVariant = 'narrow',
		): string => {
			if (is.nonZeroFalsy(price)) {
				return '';
			}
			const roundedPrice = is.number(price) ? round(price, 2) : price;
			return String(roundedPrice)
				.replaceAll(priceNumbersRegex, (match) =>
					formatPriceNumber(match, noBreakSpaceVariant),
				)
				.trim();
		},
		[formatPriceNumber],
	);
}

/**
 * Format a price with separate properties for the main number and the decimal.
 *
 * @example
 *
 * const formatPriceParts = usePricePartsFormatter();
 *
 * formatPriceParts(123)
 * // => { main: '123', decimal: '' }
 *
 * formatPriceParts(1234.5)
 * // => { main: '1 234', decimal: '50' }
 *
 * formatPriceParts('Köp 3 för 1234,5')
 * // => { main: 'Köp 3 för 1 234', decimal: '50' }
 */
export function usePricePartsFormatter() {
	const formatPrice = usePriceFormatter();

	return useCallback(
		(
			price: string | number | undefined,
			noBreakSpaceVariant: NoBreakSpaceVariant = 'narrow',
		): { decimal: string; main: string } => {
			if (!price) {
				return {
					main: price === 0 ? '0' : '',
					decimal: '',
				};
			}
			const roundedPrice = is.number(price) ? round(price, 2) : price;
			let decimal = '';
			// Find the last price number, e.g. 9.99 in 'Buy 3 for 9.99', and extract the
			// decimal from that if there is one. The price then only contains the base
			// number, e.g. 'Buy 3 for 9'.
			const cleanedPrice = String(roundedPrice).replace(
				lastPriceNumberRegex,
				(match) => {
					// Parsing a string to a number then converting back to string looks
					// stupid, but it's to handle any existing formatting; the number could
					// use a comma for either decimal or thousand separator depending on
					// locale, stringifying a JS number ensures a period.
					const parts = String(parseNumber(match)).split('.');
					decimal = parts[1] || '';
					return parts[0];
				},
			);
			return {
				main: formatPrice(cleanedPrice, noBreakSpaceVariant),
				decimal: decimal ? decimal.slice(0, 2).padEnd(2, '0') : '',
			};
		},
		[formatPrice],
	);
}

/**
 * Format a price and its symbol.
 *
 * @example
 *
 * formatPriceText(1234, '.-')
 * // => '1 234.-'
 */
export function usePriceTextFormatter() {
	const formatNumber = useNumberFormatter();

	return useCallback(
		(
			price: string | number | undefined,
			symbol: string | undefined,
		): string => {
			if (is.nonZeroFalsy(price)) {
				return '';
			}
			const formattedPrice = is.number(price) ? formatNumber(price) : price;
			return makeSpacesNoBreak(`${formattedPrice}${symbol || ''}`);
		},
		[formatNumber],
	);
}
